import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';

interface Opcao {
  id: number;
  descricao: string;
}

@Component({
  selector: 'app-cadastrar-tabela-preco-fin',
  templateUrl: './cadastrar-tabela-preco-fin.component.html',
  styleUrls: ['./cadastrar-tabela-preco-fin.component.scss']
})
export class CadastrarTabelaPrecoFinComponent implements OnInit {
  id = 0;
  descricao = '';
  valor: number | null = null;
  valorDollar: number | null = null;
  valorEuro: number | null = null;
  inicioVigencia = '';
  fimVigencia = '';
  idFinDocumento = 0;
  idFinFornecedor = 0;
  idMicroservicos = 0;
  planoContasId = 0;

  documentos: Opcao[] = [];
  fornecedores: Opcao[] = [];
  microservicos: Opcao[] = [];
  planosContas: Opcao[] = [];

  constructor(
    private ajaxService: AjaxService,
    private util: UtilService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.carregarOpcoes();
    this.route.queryParamMap.subscribe((params) => {
      this.id = Number(params.get('id') || 0);
      if (this.id > 0) this.carregar();
      else this.novo();
    });
  }

  novo(): void {
    this.id = 0;
    this.descricao = '';
    this.valor = null;
    this.valorDollar = null;
    this.valorEuro = null;
    this.inicioVigencia = '';
    this.fimVigencia = '';
    this.idFinDocumento = 0;
    this.idFinFornecedor = 0;
    this.idMicroservicos = 0;
    this.planoContasId = 0;
  }

  carregarOpcoes(): void {
    this.ajaxService.get(`${API}financeiro/FinTabelaPreco/ConsultarOpcoes`).subscribe({
      next: (response) => {
        const data = this.desempacotarResponse(response);
        this.documentos = this.normalizarLista(data.documentos).map((x: any) => ({
          id: Number(x.id || 0),
          descricao: String(x.descricao || '')
        })).filter((x: Opcao) => x.id > 0);
      }
    });

    this.ajaxService.get(`${API}empresa/TabelaValores/ConsultarFiltros`).subscribe({
      next: (response) => {
        const data = this.desempacotarResponse(response);
        this.fornecedores = this.normalizarLista(data.fornecedores).map((x: any) => ({
          id: Number(x.id || 0),
          descricao: String(x.text || x.descricao || '')
        })).filter((x: Opcao) => x.id > 0);

        this.microservicos = this.normalizarLista(data.microservicos).map((x: any) => ({
          id: Number(x.id || 0),
          descricao: String(x.text || x.descricao || '')
        })).filter((x: Opcao) => x.id > 0);
      }
    });

    this.ajaxService.get(`${API}financeiro/planocontas`).subscribe({
      next: (response) => {
        const data = this.desempacotarResponse(response);
        this.planosContas = this.normalizarLista(data).map((x: any) => ({
          id: Number(x.id || 0),
          descricao: `${String(x.codigo || '').trim()} - ${String(x.nome || '').trim()}`.replace(/^\s*-\s*/, '').trim(),
          tipo: String(x.tipo || '').trim().toUpperCase()
        }))
          .filter((x: any) => x.id > 0 && !!x.descricao && x.tipo === 'ANALITICA')
          .map((x: any) => ({ id: x.id, descricao: x.descricao }));
      }
    });
  }

  carregar(): void {
    this.util.showLoading();
    this.ajaxService.get(`${API}financeiro/FinTabelaPreco/Obter?id=${this.id}`).subscribe({
      next: (response) => {
        this.util.closeLoading();
        const data = this.desempacotarResponse(response);
        this.descricao = String(data.descricao || '');
        this.valor = data.valor != null ? Number(data.valor) : null;
        this.valorDollar = data.valordollar != null ? Number(data.valordollar) : null;
        this.valorEuro = data.valoreuro != null ? Number(data.valoreuro) : null;
        this.inicioVigencia = this.toDateInput(data.iniciovigencia);
        this.fimVigencia = this.toDateInput(data.fimvigencia);
        this.idFinDocumento = Number(data.idfindocumento || 0);
        this.idFinFornecedor = Number(data.idfinfornecedor || 0);
        this.idMicroservicos = Number(data.idmicroservicos || 0);
        this.planoContasId = Number(data.planocontasid || 0);
      },
      error: () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Não foi possível carregar a tabela de preço.');
      }
    });
  }

  salvar(): void {
    if (!this.descricao.trim()) {
      this.util.toasterShowError('Atenção', 'Descrição é obrigatória.');
      return;
    }
    if (!this.inicioVigencia) {
      this.util.toasterShowError('Atenção', 'Início de vigência é obrigatório.');
      return;
    }
    if (!this.idFinDocumento || this.idFinDocumento <= 0) {
      this.util.toasterShowError('Atenção', 'Documento é obrigatório.');
      return;
    }
    if (!this.idFinFornecedor || this.idFinFornecedor <= 0) {
      this.util.toasterShowError('Atenção', 'Fornecedor é obrigatório.');
      return;
    }
    if (!this.planoContasId || this.planoContasId <= 0) {
      this.util.toasterShowError('Atenção', 'Plano de contas é obrigatório.');
      return;
    }

    const payload = {
      id: this.id || 0,
      descricao: this.descricao,
      valor: this.valor,
      valorDollar: this.valorDollar,
      valorEuro: this.valorEuro,
      inicioVigencia: this.inicioVigencia,
      fimVigencia: this.fimVigencia || null,
      idFinDocumento: this.idFinDocumento,
      idFinFornecedor: this.idFinFornecedor,
      idMicroservicos: this.idMicroservicos && this.idMicroservicos > 0 ? this.idMicroservicos : null,
      planoContasId: this.planoContasId
    };

    this.util.showLoading();
    this.ajaxService.post(`${API}financeiro/FinTabelaPreco/Salvar`, payload).subscribe({
      next: (response) => {
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível salvar.');
          return;
        }
        this.util.toasterShowSuccess('Sucesso', this.id > 0 ? 'Tabela de preço atualizada com sucesso!' : 'Tabela de preço cadastrada com sucesso!');
        this.router.navigate(['/consultar-tabela-preco-fin']);
      },
      error: () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao salvar tabela de preço.');
      }
    });
  }

  voltar(): void {
    this.router.navigate(['/consultar-tabela-preco-fin']);
  }

  private toDateInput(value: any): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private normalizarLista(data: any): any[] {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'object') {
      if (Object.prototype.hasOwnProperty.call(data, 'id') || Object.prototype.hasOwnProperty.call(data, 'descricao')) return [data];
      return Object.values(data).filter((x: any) => x && typeof x === 'object');
    }
    return [];
  }

  private desempacotarResponse(response: any): any {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    return this.util.converterParaLowercase(atual ?? {});
  }
}
